import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { isPositiveEmotion } from "@/utils/emotionUtils";

interface EmotionChartProps {
  chartData: {
    date: string;
    intensity: number;
    emotion: string;
    timestamp?: string;
  }[];
}

// Every emotion gets a unique, visually distinct colour
const EMOTION_COLORS: Record<string, string> = {
  // App EMOTION_TYPES (primary - all unique, well-separated hues)
  calm:        "#38bdf8",  // sky blue
  excited:     "#a78bfa",  // purple
  happy:       "#4ade80",  // green
  confident:   "#f59e0b",  // amber/gold
  neutral:     "#94a3b8",  // slate
  guilty:      "#a1a1aa",  // zinc
  gulity:      "#a1a1aa",  // zinc (typo in app)
  frustrated:  "#fb923c",  // orange
  angry:       "#ef4444",  // red
  anxious:     "#facc15",  // yellow
  sad:         "#fb7185",  // rose
  overwhelmed: "#e879f9",  // fuchsia
  // Extended emotions (all distinct from above and each other)
  grateful:    "#6ee7b7",  // light emerald
  hopeful:     "#86efac",  // light green
  content:     "#22d3ee",  // cyan
  proud:       "#818cf8",  // indigo
  loved:       "#f472b6",  // pink
  stressed:    "#f97316",  // dark orange
  fear:        "#c084fc",  // violet
  nervous:     "#fdba74",  // peach
  worried:     "#fcd34d",  // light yellow
  disgust:     "#a3e635",  // lime
  grief:       "#f87171",  // light red
  depression:  "#be123c",  // deep rose
  disappointed:"#d97706",  // dark amber
  hopeless:    "#78716c",  // stone
  jealous:     "#eab308",  // gold
  hurt:        "#dc2626",  // crimson
  annoyed:     "#ea580c",  // burnt orange
  upset:       "#b91c1c",  // dark red
  shame:       "#d4d4d8",  // light grey
};

const getColor = (emotion: string) => EMOTION_COLORS[emotion?.toLowerCase()] || "#94a3b8";

// Re-export for JournalEntryForm
export const getEmotionColor = (emotion: string) => getColor(emotion);

const EmotionChart: React.FC<EmotionChartProps> = ({ chartData }) => {
  const [tooltip, setTooltip] = useState<{
    x: number; y: number; date: string; emotion: string;
    intensity: number; color: string; visible: boolean;
  }>({ x: 0, y: 0, date: "", emotion: "", intensity: 0, color: "#34d399", visible: false });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [animationTrigger, setAnimationTrigger] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!chartData.length) return;
    const withTs = chartData.filter((e) => e.timestamp);
    if (!withTs.length) return;
    const months = new Set<string>();
    withTs.forEach((e) => months.add(format(new Date(e.timestamp!), "yyyy-MM")));
    const sorted = Array.from(months).sort();
    if (sorted.length) setCurrentMonthIndex(sorted.length - 1);
  }, [chartData]);

  if (!chartData.length) return null;

  const allEntries = [...chartData]
    .filter((e) => e.timestamp)
    .sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime());

  const monthMap: Record<string, { label: string; entries: typeof allEntries }> = {};
  allEntries.forEach((entry) => {
    const d = new Date(entry.timestamp!);
    const key = format(d, "yyyy-MM");
    if (!monthMap[key]) monthMap[key] = { label: format(d, "MMM yyyy"), entries: [] };
    monthMap[key].entries.push(entry);
  });

  const monthKeys = Object.keys(monthMap).sort();
  if (!monthKeys.length) return null;

  const safeIdx = Math.min(currentMonthIndex, monthKeys.length - 1);
  const activeMonth = monthMap[monthKeys[safeIdx]];
  const monthEntries = activeMonth.entries;

  // Group by date, average per date
  const byDate: Record<string, typeof monthEntries> = {};
  monthEntries.forEach((e) => {
    if (!byDate[e.date]) byDate[e.date] = [];
    byDate[e.date].push(e);
  });

  const points = Object.entries(byDate)
    .map(([date, entries]) => ({
      date,
      intensity: entries.reduce((s, e) => s + e.intensity, 0) / entries.length,
      emotion: entries[0].emotion,
      timestamp: entries[0].timestamp,
    }))
    .sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime());

  const emptyMonth = !points.length;

  // Dimensions
  const pad = { top: 30, right: 25, bottom: 50, left: 45 };
  const minWPerPoint = 90;
  const width = Math.max(containerWidth, points.length * minWPerPoint + pad.left + pad.right, 400);
  const height = 340;
  const gw = width - pad.left - pad.right;
  const gh = height - pad.top - pad.bottom;

  const times = points.map((p) => new Date(p.timestamp!).getTime());
  const tMin = times.length ? Math.min(...times) : 0;
  const tMax = times.length ? Math.max(...times) : 1;

  const getX = (ts?: string) => {
    if (!ts) return pad.left;
    const t = new Date(ts).getTime();
    if (tMin === tMax) return pad.left + gw / 2;
    return pad.left + ((t - tMin) / (tMax - tMin)) * gw;
  };

  const getY = (intensity: number) => pad.top + gh - (intensity / 10) * gh;

  // Build one smooth curve through all points
  const buildCurve = () => {
    if (points.length === 1) {
      return `M ${getX(points[0].timestamp)} ${getY(points[0].intensity)}`;
    }
    return points.reduce((acc, p, i, arr) => {
      const x = getX(p.timestamp);
      const y = getY(p.intensity);
      if (i === 0) return `M ${x} ${y}`;
      const px = getX(arr[i - 1].timestamp);
      const py = getY(arr[i - 1].intensity);
      const cx = (px + x) / 2;
      return acc + ` C ${cx} ${py}, ${cx} ${y}, ${x} ${y}`;
    }, "");
  };

  const curvePath = buildCurve();
  const bottomY = pad.top + gh;
  const areaPath = points.length >= 2
    ? `${curvePath} L ${getX(points[points.length - 1].timestamp)} ${bottomY} L ${getX(points[0].timestamp)} ${bottomY} Z`
    : "";

  const yTicks = [0, 2, 4, 6, 8, 10];
  const uniqueEmotions = Array.from(new Set(points.map((p) => p.emotion.toLowerCase())));

  // Growth animation: segment-by-segment draw, then dots pop in
  const nSegments = Math.max(points.length - 1, 1);
  const segmentDuration = 3.0 / nSegments; // Slower: ~3 seconds total for line
  const totalAnimationSec = nSegments * segmentDuration + 0.8; // Extra time for final dot

  useEffect(() => {
    if (animationTrigger <= 0) return;
    const t = setTimeout(() => setAnimationTrigger(0), totalAnimationSec * 1000);
    return () => clearTimeout(t);
  }, [animationTrigger, totalAnimationSec]);

  // Auto-run growth animation whenever the user switches to a different month
  useEffect(() => {
    setAnimationTrigger((t) => t + 1);
  }, [safeIdx]);

  return (
    <Card className="mb-8 shadow-md border border-[#CCCCCC] bg-white animate-fadeIn">
      <CardHeader className="bg-[#E8F0F8] rounded-t-lg">
        <CardTitle className="text-[#333B44]">EmoGraph</CardTitle>
        <CardDescription className="text-[#5A636E]">Your emotional journey over time</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {/* Month nav */}
        <div className="px-4 sm:px-6 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"
              onClick={() => setCurrentMonthIndex((i) => Math.max(0, i - 1))}
              disabled={safeIdx === 0}>◀</Button>
            <span className="text-sm font-medium text-[#333B44]">{activeMonth.label}</span>
            <Button variant="outline" size="sm"
              onClick={() => setCurrentMonthIndex((i) => Math.min(monthKeys.length - 1, i + 1))}
              disabled={safeIdx === monthKeys.length - 1}>▶</Button>
          </div>
        </div>

        {emptyMonth ? (
          <div className="py-12 text-center text-sm text-[#5A636E]">
            No journal entries recorded in this month.
          </div>
        ) : (
          <>
            <div ref={containerRef} className="w-full overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
              <div
                className="relative rounded-xl mx-3 sm:mx-5 mb-2"
                style={{ background: "linear-gradient(135deg, #0f1629 0%, #131b3a 50%, #1a1145 100%)" }}
              >
                {/* Grid overlay */}
                <div className="absolute inset-0 rounded-xl opacity-[0.05] pointer-events-none" style={{
                  backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                  backgroundSize: `${gw / 6}px ${gh / 5}px`,
                  backgroundPosition: `${pad.left}px ${pad.top}px`,
                }} />

                <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ minWidth: width }}>
                  <defs>
                    {/* Per-segment line gradients that blend between emotion colours */}
                    {points.map((p, i) => {
                      if (i >= points.length - 1) return null;
                      const x1 = getX(p.timestamp);
                      const y1 = getY(p.intensity);
                      const x2 = getX(points[i + 1].timestamp);
                      const y2 = getY(points[i + 1].intensity);
                      const c1 = getColor(p.emotion);
                      const c2 = getColor(points[i + 1].emotion);
                      return (
                        <linearGradient key={`seg-${i}`} id={`seg-${i}`}
                          x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor={c1} />
                          <stop offset="100%" stopColor={c2} />
                        </linearGradient>
                      );
                    })}
                    {/* Area fill that also blends horizontally */}
                    <linearGradient id="areaFillH" x1="0%" y1="0%" x2="100%" y2="0%">
                      {points.map((p, i) => (
                        <stop key={i} offset={`${(i / Math.max(points.length - 1, 1)) * 100}%`}
                          stopColor={getColor(p.emotion)} stopOpacity="0.15" />
                      ))}
                    </linearGradient>
                    <linearGradient id="areaFillV" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="white" stopOpacity="1" />
                      <stop offset="100%" stopColor="white" stopOpacity="0.05" />
                    </linearGradient>
                    <mask id="areaMask">
                      <rect x="0" y="0" width={width} height={height} fill="url(#areaFillV)" />
                    </mask>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  {/* Y grid lines */}
                  {yTicks.map((v) => (
                    <line key={v} x1={pad.left} y1={getY(v)} x2={width - pad.right} y2={getY(v)}
                      stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
                  ))}

                  {/* Y labels */}
                  {yTicks.map((v) => (
                    <text key={`yl-${v}`} x={pad.left - 8} y={getY(v) + 4} textAnchor="end"
                      fontSize="10" fill="rgba(255,255,255,0.3)">{v}</text>
                  ))}

                  {/* X date labels */}
                  {points.map((p) => (
                    <text key={`xl-${p.date}`} x={getX(p.timestamp)} y={height - pad.bottom + 20}
                      textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.35)">
                      {format(new Date(p.timestamp!), "dd MMM")}
                    </text>
                  ))}

                  {/* Area fill - blends emotion colours horizontally, fades out vertically */}
                  {areaPath && <path d={areaPath} fill="url(#areaFillH)" mask="url(#areaMask)" />}

                  {/* Glow shadow underneath the curve for depth */}
                  {curvePath && animationTrigger > 0 ? (
                    <motion.path
                      key={`glow-${animationTrigger}`}
                      d={curvePath}
                      fill="none"
                      stroke="rgba(255,255,255,0.12)"
                      strokeWidth={10}
                      strokeLinecap="round"
                      filter="url(#glow)"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: totalAnimationSec * 0.85, ease: "easeInOut" }}
                    />
                  ) : curvePath ? (
                    <path d={curvePath} fill="none" stroke="rgba(255,255,255,0.12)"
                      strokeWidth={10} strokeLinecap="round" filter="url(#glow)" />
                  ) : null}

                  {/* White outline under segments for contrast on dark bg */}
                  {curvePath && animationTrigger > 0 ? (
                    <motion.path
                      key={`outline-${animationTrigger}`}
                      d={curvePath}
                      fill="none"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth={5}
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: totalAnimationSec * 0.85, ease: "easeInOut" }}
                    />
                  ) : curvePath ? (
                    <path d={curvePath} fill="none" stroke="rgba(255,255,255,0.2)"
                      strokeWidth={5} strokeLinecap="round" />
                  ) : null}

                  {/* Curve line segments - draw one dot to the next */}
                  {animationTrigger > 0 ? (
                    points.map((p, i) => {
                      if (i >= points.length - 1) return null;
                      const next = points[i + 1];
                      const x1 = getX(p.timestamp);
                      const y1 = getY(p.intensity);
                      const x2 = getX(next.timestamp);
                      const y2 = getY(next.intensity);
                      const cx = (x1 + x2) / 2;
                      const segPath = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
                      return (
                        <motion.path
                          key={`line-${animationTrigger}-${i}`}
                          d={segPath}
                          fill="none"
                          stroke={`url(#seg-${i})`}
                          strokeWidth={3.5}
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{
                            duration: segmentDuration,
                            delay: i * segmentDuration,
                            ease: "easeInOut",
                          }}
                        />
                      );
                    })
                  ) : (
                    points.map((p, i) => {
                      if (i >= points.length - 1) return null;
                      const next = points[i + 1];
                      const x1 = getX(p.timestamp);
                      const y1 = getY(p.intensity);
                      const x2 = getX(next.timestamp);
                      const y2 = getY(next.intensity);
                      const cx = (x1 + x2) / 2;
                      const segPath = `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
                      return (
                        <path key={`line-${i}`} d={segPath} fill="none" stroke={`url(#seg-${i})`}
                          strokeWidth={3.5} strokeLinecap="round" />
                      );
                    })
                  )}

                  {/* Data dots - appear as the line reaches each point */}
                  {animationTrigger > 0 ? (
                    points.map((p, idx) => {
                      const x = getX(p.timestamp);
                      const y = getY(p.intensity);
                      const color = getColor(p.emotion);
                      const delay = idx * segmentDuration;
                      return (
                        <motion.g
                          key={`dot-${animationTrigger}-${idx}`}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            delay,
                            duration: 0.6,
                            ease: [0.22, 0.61, 0.36, 1],
                          }}
                        >
                          <circle cx={x} cy={y} r={9} fill={color} opacity={0.12} />
                          <circle
                            cx={x} cy={y} r={5} fill={color}
                            stroke="rgba(255,255,255,0.8)" strokeWidth={1.5}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => {
                              const tipY = y < 80 ? y + 20 : y - 65;
                              setTooltip({ x, y: tipY, date: p.date, emotion: p.emotion, intensity: p.intensity, color, visible: true });
                            }}
                            onMouseLeave={() => setTooltip((prev) => ({ ...prev, visible: false }))}
                          />
                        </motion.g>
                      );
                    })
                  ) : (
                    points.map((p, idx) => {
                      const x = getX(p.timestamp);
                      const y = getY(p.intensity);
                      const color = getColor(p.emotion);
                      return (
                        <g key={`dot-${idx}`}>
                          <circle cx={x} cy={y} r={9} fill={color} opacity={0.12} />
                          <circle
                            cx={x} cy={y} r={5} fill={color}
                            stroke="rgba(255,255,255,0.8)" strokeWidth={1.5}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => {
                              const tipY = y < 80 ? y + 20 : y - 65;
                              setTooltip({ x, y: tipY, date: p.date, emotion: p.emotion, intensity: p.intensity, color, visible: true });
                            }}
                            onMouseLeave={() => setTooltip((prev) => ({ ...prev, visible: false }))}
                          />
                        </g>
                      );
                    })
                  )}

                  {/* Tooltip */}
                  {tooltip.visible && (
                    <g transform={`translate(${Math.max(pad.left, Math.min(tooltip.x - 75, width - 170))},${Math.max(5, tooltip.y)})`}>
                      <rect x={0} y={0} width={155} height={52} rx={10}
                        fill="rgba(15,22,41,0.92)" stroke={tooltip.color} strokeWidth={1.5} />
                      <text x={12} y={20} fontSize={11} fill="rgba(255,255,255,0.6)" fontWeight="500">
                        {tooltip.date}
                      </text>
                      <text x={12} y={40} fontSize={14} fontWeight="bold" fill={tooltip.color}>
                        {tooltip.emotion}
                      </text>
                      <text x={12 + tooltip.emotion.length * 8.5} y={40} fontSize={12} fill="rgba(255,255,255,0.5)">
                        {tooltip.intensity.toFixed(1)}/10
                      </text>
                    </g>
                  )}
                </svg>
              </div>
            </div>

            {/* Legend */}
            <div className="px-4 sm:px-6 py-3 flex flex-wrap justify-center gap-3 sm:gap-4 text-xs">
              {uniqueEmotions.map((emotion) => {
                const color = getColor(emotion);
                const positive = isPositiveEmotion(emotion);
                return (
                  <div key={emotion} className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }} />
                    <span className="text-[#5A636E] capitalize">{emotion}</span>
                    <span className={`text-[10px] ${positive ? "text-emerald-500" : "text-rose-400"}`}>
                      {positive ? "+" : "−"}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmotionChart;
